<template>
  <div class="common-image">
    <el-image
      :src="src"
      :alt="alt"
      :fit="fit"
      :lazy="lazy"
      :preview-src-list="previewSrcList"
      :initial-index="initialIndex"
      :z-index="zIndex"
      :preview-teleported="previewTeleported"
      :hide-on-click-modal="hideOnClickModal"
      @load="handleLoad"
      @error="handleError"
      @switch="handleSwitch"
      @close="handleClose"
      @show="handleShow"
      class="image-content"
    >
      <!-- 自定义加载插槽 -->
      <template #placeholder>
        <div class="image-placeholder">
          <el-icon class="is-loading">
            <Loading />
          </el-icon>
          <span v-if="loadingText">{{ loadingText }}</span>
        </div>
      </template>

      <!-- 自定义错误插槽 -->
      <template #error>
        <div class="image-error-slot">
          <img :src="getImage(errorImage)" v-if="errorImage" class="image-error-logo" />
          <template v-else>
            <el-icon class="error-icon">
              <Picture />
            </el-icon>
            <span v-if="errorText">{{ errorText }}</span>
          </template>
        </div>
      </template>
    </el-image>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { ElImage, ElIcon } from 'element-plus';
import { Loading, Picture } from '@element-plus/icons-vue';
import { getImage } from '@/utils';

interface Props {
  // 图片源
  src: string;
  // 替代文本
  alt?: string;
  // 图片适应方式
  fit?: 'fill' | 'contain' | 'cover' | 'none' | 'scale-down';
  // 是否懒加载
  lazy?: boolean;
  // 是否开启预览
  preview?: boolean;
  // 预览图片列表
  previewSrcList?: string[];
  // 预览初始索引
  initialIndex?: number;
  // 预览层级
  zIndex?: number;
  // 预览是否传送到 body
  previewTeleported?: boolean;
  // 点击遮罩层是否关闭预览
  hideOnClickModal?: boolean;
  // 失败图片（可选，如果不传则使用默认图标）
  errorImage?: string;
  // 加载文本
  loadingText?: string;
  // 错误文本
  errorText?: string;
}

const props = withDefaults(defineProps<Props>(), {
  alt: '',
  fit: 'cover',
  lazy: false,
  preview: false,
  previewSrcList: () => [],
  initialIndex: 0,
  zIndex: 2000,
  previewTeleported: true,
  hideOnClickModal: true,
  errorImage: '',
  loadingText: '加载中...',
  errorText: '加载失败',
});

const emit = defineEmits<{
  load: [event: Event];
  error: [event: Event];
  switch: [index: number];
  close: [];
  show: [];
}>();

// 计算属性
const previewSrcList = computed(() => {
  if (props.preview && props.previewSrcList.length > 0) {
    return props.previewSrcList;
  }
  return props.preview ? [props.src] : [];
});

// 事件处理
const handleLoad = (event: Event) => {
  emit('load', event);
};

const handleError = (event: Event) => {
  emit('error', event);
};

const handleSwitch = (index: number) => {
  emit('switch', index);
};

const handleClose = () => {
  emit('close');
};

const handleShow = () => {
  emit('show');
};
</script>

<style lang="scss" scoped>
.common-image {
  position: relative;
  display: inline-block;
  width: 100%;
  height: 100%;
  overflow: hidden;

  .image-content {
    width: 100%;
    height: 100%;
  }

  .image-placeholder,
  .image-error-slot {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background-color: #f5f7fa;
    color: #909399;
    font-size: 14px;
    gap: 8px;

    .el-icon {
      font-size: 32px;
      color: #c0c4cc;
    }

    .error-icon {
      color: #c0c4cc;
      font-size: 32px;
    }
  }
  &::v-deep {
    .el-image__inner {
      width: 100%;
      height: 100%;
    }
  }
}

.image-error-logo {
  width: 100%;
  height: 100%;
}

// 加载动画
:deep(.el-icon.is-loading) {
  animation: rotating 2s linear infinite;
}

@keyframes rotating {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
